import { UIInsurance, UIProperty } from '@api/v1';
import { TableRow, TableCell, IconButton, Box } from '@mui/material';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transformDamageType } from './utils';
import moment from 'moment-with-locales-es6';
import { createInsuranceStateKey } from '../../lib/i18keyGenerator';
import { PropertySelect } from './PropertySelect';
import { useDispatch } from 'react-redux';
import { updateInsurance } from './actions';

interface InsuranceListEntryProps {
	insurance: UIInsurance;
	onEditClick: (insurance: UIInsurance) => void;
	searchInput: string;
	pageIndex: number;
	pageSize: number;
}

export function InsuranceListEntry({
	insurance,
	onEditClick,
	searchInput,
	pageIndex,
	pageSize
}: InsuranceListEntryProps) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const damageTypes = insurance.damageType.map((type) => type.value || type);
	const damageTypeLabels = transformDamageType(damageTypes, t).map(({ label }) => label);

	const [selectedProperties, setSelectedProperties] = useState<UIProperty[]>(
		insurance.properties || []
	);
	const [isUpdating, setIsUpdating] = useState(false);

	const hasChanges =
		selectedProperties.length !== (insurance.properties?.length ?? 0) ||
		selectedProperties.some((p) => !(insurance.properties ?? []).find((op) => op.id === p.id));

	const handleApply = useCallback(() => {
		if (isUpdating) {
			return;
		}

		setIsUpdating(true);
		const updatedInsurance: UIInsurance = {
			...insurance,
			properties: selectedProperties,
			damageType: transformDamageType(damageTypes, t)
		};
		return dispatch(updateInsurance(pageIndex, pageSize, searchInput, updatedInsurance)).finally(
			() => {
				setIsUpdating(false);
			}
		);
	}, [
		insurance,
		selectedProperties,
		damageTypes,
		pageIndex,
		pageSize,
		searchInput,
		dispatch,
		t,
		isUpdating
	]);

	const handleCancel = useCallback(() => {
		if (isUpdating) {
			return;
		}
		setSelectedProperties(insurance.properties || []);
	}, [insurance.properties, isUpdating]);

	return (
		<TableRow sx={{ pr: 10 }}>
			<TableCell>{insurance.externalId}</TableCell>
			<TableCell>{!!insurance.status && t(createInsuranceStateKey(insurance.status))}</TableCell>
			<TableCell>{insurance.customer?.name}</TableCell>
			<TableCell sx={{ minWidth: 250 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<PropertySelect
						customerId={insurance.customer?.id || null}
						value={selectedProperties}
						onChange={setSelectedProperties}
						size="small"
						disabled={isUpdating}
						description={insurance.description}
					/>
					{hasChanges && (
						<>
							<IconButton
								size="small"
								color="primary"
								onClick={handleApply}
								disabled={selectedProperties.length === 0 || isUpdating}
							>
								<CheckIcon fontSize="small" />
							</IconButton>
							<IconButton size="small" color="error" onClick={handleCancel} disabled={isUpdating}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</>
					)}
				</Box>
			</TableCell>
			<TableCell>{insurance.description}</TableCell>
			<TableCell>{damageTypeLabels.join(', ')}</TableCell>
			<TableCell>
				{insurance.expirationDate ? moment(insurance.expirationDate).format('DD.MM.YYYY') : ''}
			</TableCell>
			<TableCell>
				<IconButton color="primary" onClick={() => onEditClick(insurance)} size="large">
					<EditIcon />
				</IconButton>
			</TableCell>
		</TableRow>
	);
}
